/**
 * Paged-view state.
 *
 * Every paged collection in this platform has the same failure mode: change a
 * filter while on page 4 and you land on page 4 of a result set that may only
 * have one page, which reads as "my search found nothing". This hook makes the
 * reset automatic — the page number is keyed to the filters, so any change to
 * them returns to page 1 while the filters themselves are untouched.
 *
 * The filters are the caller's own state; this only owns the page number.
 *
 * Extracted from the Cascade portal so every portal's lists behave the same.
 */

import { useState } from 'react';

export const DEFAULT_VIEW_PAGE_SIZE = 25;

export function usePagedView(
  /** Everything that changes the result set: search text, status, view, sort. */
  filters: ReadonlyArray<unknown>,
  pageSize: number = DEFAULT_VIEW_PAGE_SIZE,
) {
  const [page, setPage] = useState(1);
  const [appliedKey, setAppliedKey] = useState(() => JSON.stringify(filters));
  const key = JSON.stringify(filters);

  /*
    Reset during render, not in an effect. An effect runs after the render has
    already been committed, which means the query for "page 4 of the new filter"
    fires and is thrown away one beat later — a wasted round trip on every
    keystroke in a search box. Adjusting state during render re-renders before
    anything commits, so only the correct request is ever made.
  */
  if (appliedKey !== key) {
    setAppliedKey(key);
    setPage(1);
  }

  return { page: appliedKey === key ? page : 1, setPage, pageSize };
}
